import {createElement} from 'react';
import {createRoot, Root} from 'react-dom/client';
import {
  RESERVATION_DETAILS,
  EXTERNAL_ID_KEY,
  RESERVATION_ID_KEY,
  SDK_STYLES,
  SDK_STYLES_LINK,
  SDK_MODE,
} from './utils/embedded';
import {mapAndValidateGuestFormData} from './utils/sdk';
import {LocalStorage} from '@guestapp/core';
import SDKApp from './SDKApp';
import {environments} from 'configs';

type InitialAppProps = Window['ChekinProSettings'];

type RenderAppProps = {
  targetNode: string;
};

type InitAndRenderProps = InitialAppProps & RenderAppProps;

const mockCallback = () => {};

const IS_SDK_MODE = environments.VITE_APP_IS_SDK;

class ChekinPro {
  private root: Root | null = null;
  private clearLocalStorage() {
    LocalStorage.remove(RESERVATION_ID_KEY);
    LocalStorage.remove(EXTERNAL_ID_KEY);
    LocalStorage.remove(RESERVATION_DETAILS);
    LocalStorage.remove(SDK_STYLES);
    LocalStorage.remove(SDK_STYLES_LINK);
  }

  validateInitProps({
    apiKey,
    guestId,
    reservationId,
    housingId,
    externalId,
    mode,
  }: InitialAppProps) {
    if (!apiKey) {
      throw new Error('API key is required');
    } else if (guestId && mode !== SDK_MODE.onlyGuestForm) {
      throw new Error('The guestId can be used only in "ONLY_GUEST_FORM" mode');
    } else if (guestId && !externalId && !reservationId) {
      throw new Error('The guestId should be passed with reservationId or externalId');
    } else if (!housingId && mode === SDK_MODE.propertyLink) {
      throw new Error('The housingId is required in "PROPERTY_LINK" mode');
    } else if (housingId && mode !== SDK_MODE.propertyLink) {
      throw new Error('The housingId can be used only in "PROPERTY_LINK" mode');
    }
  }

  initialize(props: InitialAppProps) {
    const {
      mode = SDK_MODE.all,
      apiKey,
      styles,
      autoHeight,
      stylesLink,
      externalId,
      guestId,
      reservationId,
      housingId,
      externalHousingId,
      defaultLanguage,
      enableGuestsRemoval,
      prefillData,
      onError,
      onMounted,
      onHeightChanged,
      onGuestRegistered,
      onAllGuestsRegistered,
      onConnectionError,
      onReservationFound,
      onReservationFetched,
      onReservationCreated,
      onReservationFoundFromHousing,
      onScreenChanged,
      onIVFinished,
      canShareRegistrationLink,
      canEditReservationDetails = true,
    } = props || {};
    this.validateInitProps(props);
    this.clearLocalStorage();
    LocalStorage.set(RESERVATION_ID_KEY, reservationId);
    LocalStorage.set(EXTERNAL_ID_KEY, externalId);
    LocalStorage.set(RESERVATION_DETAILS, canEditReservationDetails);
    LocalStorage.set(SDK_STYLES, styles || '');
    LocalStorage.set(SDK_STYLES_LINK, stylesLink || '');

    window.ChekinProSettings = {
      mode,
      apiKey,
      styles,
      guestId,
      autoHeight,
      stylesLink,
      externalId: externalId || undefined,
      reservationId: reservationId || undefined,
      housingId: housingId || undefined,
      externalHousingId: externalHousingId || undefined,
      defaultLanguage,
      onError,
      onMounted,
      prefillData: {
        guestForm: mapAndValidateGuestFormData(prefillData?.guestForm),
      },
      onIVFinished,
      onHeightChanged,
      onScreenChanged,
      onConnectionError,
      onGuestRegistered,
      onReservationFound,
      onReservationFetched,
      onAllGuestsRegistered,
      enableGuestsRemoval,
      onReservationCreated,
      onReservationFoundFromHousing,
      canShareRegistrationLink,
      canEditReservationDetails,
    };
    console.log(`The ${__APP_NAME__} SDK ${__APP_VERSION__} is initialized`);
  }

  static getSettings() {
    if (!IS_SDK_MODE) {
      return {};
    }

    const reservationId =
      window?.ChekinProSettings?.reservationId || LocalStorage.get(RESERVATION_ID_KEY);
    const apiKey = window?.ChekinProSettings?.apiKey;
    const externalId =
      window?.ChekinProSettings?.externalId || LocalStorage.get(EXTERNAL_ID_KEY);
    const defaultLanguage = window?.ChekinProSettings?.defaultLanguage;
    const enableGuestsRemoval = window?.ChekinProSettings?.enableGuestsRemoval;
    const mode = window?.ChekinProSettings?.mode;
    const prefillData = window?.ChekinProSettings?.prefillData;

    const canEditReservationDetails =
      window?.ChekinProSettings?.canEditReservationDetails ||
      Boolean(LocalStorage.get(RESERVATION_DETAILS));

    const canShareRegistrationLink = window?.ChekinProSettings?.canShareRegistrationLink;

    const styles = window?.ChekinProSettings?.styles || LocalStorage.get(SDK_STYLES);
    const stylesLink =
      window?.ChekinProSettings?.stylesLink || LocalStorage.get(SDK_STYLES_LINK);

    return {
      mode,
      apiKey,
      styles,
      stylesLink,
      externalId,
      prefillData,
      reservationId,
      defaultLanguage,
      enableGuestsRemoval,
      canShareRegistrationLink,
      canEditReservationDetails,
    };
  }

  static get events() {
    const settings = window.ChekinProSettings || {};

    return {
      onMounted: settings?.onMounted || mockCallback,
      onReservationFound: settings?.onReservationFound || mockCallback,
      onReservationCreated: settings?.onReservationCreated || mockCallback,
      onReservationFoundFromHousing:
        settings?.onReservationFoundFromHousing || mockCallback,
      onReservationFetched: settings?.onReservationFetched || mockCallback,
      onGuestRegistered: settings?.onGuestRegistered || mockCallback,
      onAllGuestsRegistered: settings?.onAllGuestsRegistered || mockCallback,
      onConnectionError: settings?.onConnectionError || mockCallback,
      onScreenChanged: settings?.onScreenChanged || mockCallback,
      onHeightChanged: settings?.onHeightChanged || mockCallback,
      onIVFinished: settings?.onIVFinished || mockCallback,
      onError: settings?.onError || mockCallback,
    };
  }

  renderApp({targetNode}: RenderAppProps) {
    const reactElement = createElement(SDKApp, {}, 'Chekin-Pro');
    const container = document.getElementById(targetNode);

    if (!container) {
      throw new Error('The target element by id not found');
    }

    const root = createRoot(container);
    this.root = root;
    root.render(reactElement);
    return reactElement;
  }

  inject() {
    return SDKApp;
  }

  unmount() {
    console.log(`The ${__APP_NAME__} SDK ${__APP_VERSION__} is unmounted`);
    return this.root?.unmount();
  }

  initAndRender(props: InitAndRenderProps) {
    this.initialize(props);
    this.renderApp({targetNode: props.targetNode});
  }
}

export default ChekinPro;
